import React, {
  useEffect,
  useState,
  forwardRef,
  SyntheticEvent,
  ReactNode,
  ComponentPropsWithRef,
  MouseEventHandler,
} from 'react';

import styled from '@emotion/styled';
import { variant } from '@lucid/theme';
import {
  isAnchorPath,
  getInternalLink,
  sendBeaconEvent,
  withCookieSyncParams,
  useRedirects,
  useSiteMetadata,
  localizePhoneNumbers,
  withCookieUrlParams,
  usePathPrefix,
  isBrowser,
} from '@lucid/utils';
import { navigate, withPrefix } from 'gatsby';

import { Component, Beacon } from '../../types/types';
import decorate from '../decorate/decorate';
import Button, { ButtonTypes, ButtonOptions } from '../button/button';
import Icon from '../icon/icon';
import StyledGatsbyLink from './styled-gatsby-link';

export type LinkUnderlineType = 'none' | 'hover' | 'always';

export interface BaseLinkOptions {
  /** The children for the Link component.  */
  children?: ReactNode;
  /** Allows the component to get additional styles. */
  className?: string;
  /** Sets the link to `display="block"` for the link. */
  displayBlock?: boolean;
  /** Render an arrow on the right of the link. */
  hasArrow?: boolean;
  /** Determines if the link is active. */
  isActive?: boolean;
  /** Sets the color of the arrow icon. */
  iconColor?: string;
  /** Checks if the link should be disabled. */
  isDisabled?: boolean;
  /** Sets the color of the arrow icon onHover. */
  iconHoverColor?: string;
  /** Checks if the link should have inverse styles. */
  isInverse?: boolean;
  /** Sets the underline value for the Link. */
  underline?: LinkUnderlineType;
}

export interface LinkOptions {
  /** The link used in the Link component. */
  href?: string;
  /** The label used for the link or button, can also be passed
   * into the component as `children`
   */
  label?: string;
  /** The link used in the Link component. */
  to?: string;
}

type LinkElementHTMLProps = Omit<ComponentPropsWithRef<'a'>, 'as' | 'size' | 'type'>;

export interface LinkProps extends LinkOptions, BaseLinkOptions, LinkElementHTMLProps, ButtonOptions {
  /** Render a `button` instead of `a` link tag. */
  asButton?: boolean;
  /** The beacon event config. */
  beaconEvent?: Beacon.Event | null;
  /** Extends a list of url paths that should not be considered as internal links. */
  blocklist?: string[];
  /** Will set link `rel="noopener noreferrer"` and `target="_blank"` */
  externalAlwaysNewTab?: boolean;
  /** Will create an HTML `a` tag, instead of a GatsbyLink */
  excludeFromGatsby?: boolean | null;
  /** Add the name HTML attribute to the Link component. */
  name?: string;
  /** Will open the link in a new tab, by setting
   * `target="_blank"`
   */
  newTab?: boolean;
  /** Checks if the link should be prefixed */
  noPrefix?: boolean;
  /** Specifies the relationship between
   * the current document and the linked document/resource.
   */
  rel?: string;
  /** Outlines the behavior for scrolling to same-page anchors */
  scrollToOptions?: ScrollIntoViewOptions;
  /** TODO: Add TS Doc Info Here */
  shouldCheckHistory?: boolean;
  /** Allows for tabbing on keyboard to change links. */
  tabIndex?: number;
  /** Sets the link target */
  target?: string;
  /** Adds beacon params for cookie sync regardless of url. */
  willAddBeaconParams?: boolean;
  /** Adds passed in cookies to the link url params. */
  withCookieParams?: string[];
  /** The available variants for the Link component. */
  variant?: Component.Type['Link'] | Component.Type['Button'];
}

const StyledLink = styled.a<LinkProps>(
  variant({
    scale: 'links',
    variants: {
      defaultVariant: {},
    },
  }),
  ({ theme, underline, displayBlock, hasArrow, isInverse }) => [
    {
      label: 'link',
      display: 'inline-block',
      fontWeight: theme.fontWeights.strong,
      textDecoration: 'none',
      cursor: 'pointer',
    },
    isInverse && {
      color: theme.colors.white,
    },
    underline === 'always' && {
      textDecoration: 'underline',
    },
    underline === 'always' ||
      (underline === 'hover' && {
        '&:hover, &:active, &:focus': {
          textDecoration: 'underline',
        },
      }),
    displayBlock && {
      display: 'block',
    },
    hasArrow && {
      '&:hover, &:focus': {
        '& > .hasArrow': {
          transform: 'translateX(0.15rem)',
        },
      },
    },
  ],
);

const LinkComponent = forwardRef<HTMLAnchorElement, LinkProps>(
  (
    {
      asButton = false,
      beaconEvent,
      blocklist = [],
      className,
      displayAsButton = false,
      displayBlock = false,
      externalAlwaysNewTab = false,
      excludeFromGatsby = false,
      children,
      hasArrow = false,
      href,
      iconColor,
      isDisabled = false,
      iconHoverColor,
      isInverse = false,
      label,
      newTab = false,
      noPrefix = false,
      rel,
      scrollToOptions,
      shouldCheckHistory = false,
      target,
      onClick,
      to,
      type,
      underline = 'none',
      willAddBeaconParams = false,
      withCookieParams = [],
      variant = 'primary',
      ...rest
    },
    forwardedRef,
  ) => {
    const resolveRedirects = useRedirects();
    const pathPrefix = usePathPrefix();
    const { product, siteDomain } = useSiteMetadata();
    const redirect = resolveRedirects(to || href || '');
    const baseLink = redirect || to || href;
    const hrefToValue = baseLink || undefined;
    const checkLink = getInternalLink({
      link: hrefToValue,
      pathPrefix,
      siteDomain,
      blocklist,
    });
    const isInternalLink = checkLink.isInternal || false;
    const link = isInternalLink ? checkLink?.url : baseLink;
    const [hrefTo, setHrefTo] = useState(link);

    useEffect(() => {
      const hrefToLink = withCookieSyncParams(link, willAddBeaconParams);
      const hrefToLinkWithCookieParams = withCookieUrlParams(hrefToLink, withCookieParams);

      if (!hrefToLinkWithCookieParams) {
        return setHrefTo(hrefToLinkWithCookieParams);
      }

      if (hrefTo?.startsWith('tel:')) {
        const formattedHrefTo = localizePhoneNumbers(hrefTo, product);
        return setHrefTo(formattedHrefTo);
      }

      return setHrefTo(hrefToLinkWithCookieParams);
    }, [link, hrefTo, product, withCookieParams, willAddBeaconParams]);

    try {
      if (asButton && !onClick && !type) {
        throw `You must provide either an 'onClick' function
        or a 'type' when Link prop 'asButton' is true for ${hrefTo}.`;
      }

      if (displayAsButton && !hrefTo && !onClick) {
        throw `You must provide either a 'href', 'to', or 'onClick' function
        when Link prop 'displayAsButton' is true.`;
      }
    } catch (error) {
      throw new Error(`${error}`);
    }

    const linkLabel = label ? label : children;
    const hasNoFollow = hrefTo && !isInternalLink && hrefTo.includes('lucid.app');
    const newTabProps =
      newTab || externalAlwaysNewTab
        ? { target: '_blank', rel: 'noopener noreferrer' }
        : target
        ? { target }
        : undefined;

    const linkRelProps = rel
      ? newTabProps?.rel
        ? `${newTabProps?.rel} ${rel}`.trim()
        : rel.trim()
      : newTabProps?.rel
      ? newTabProps?.rel.trim()
      : undefined;

    const relProps = hasNoFollow
      ? `nofollow ${linkRelProps ? linkRelProps : ''}`.trim()
      : linkRelProps
      ? linkRelProps
      : undefined;

    const targetProps = target || newTabProps?.target ? newTabProps?.target : undefined;

    // Utillize the window 'back' functionality, which will keep previous query
    // params, rather than navigate back to the previous page loaded new
    // without query params.
    const beforeNavigation = (e: SyntheticEvent<Element, Event>) => {
      if (shouldCheckHistory) {
        const prevLocation = sessionStorage.getItem('prevLocation');
        const baseTo = hrefTo && hrefTo.split('?')[0];

        if (prevLocation && baseTo?.startsWith(prevLocation)) {
          e.preventDefault();
          window.history.back();
        }
      }
    };

    const handleOnClick: MouseEventHandler<HTMLAnchorElement> = e => {
      if (beaconEvent) {
        sendBeaconEvent(beaconEvent);
      }

      beforeNavigation(e);

      if (onClick) {
        onClick(e);
      }

      if (hrefTo && isAnchorPath(hrefTo) && !onClick) {
        e.preventDefault();
        if (!scrollToOptions) {
          navigate(hrefTo);
        }
        if (isBrowser()) {
          const el = document.getElementById(hrefTo.slice(1));
          if (el) {
            el.scrollIntoView(scrollToOptions);
            // move keyboard focus for screenreaders
            el.tabIndex = -1;
            el.focus({ preventScroll: true });
          }
        }
      }
    };

    const currentHrefToValue = isInternalLink && !noPrefix && hrefTo ? withPrefix(hrefTo) : hrefTo;

    if (displayAsButton || asButton) {
      const buttonTag = asButton ? 'button' : 'a';
      const buttonSize = rest.size as unknown as Component.Type['ButtonSizes'];
      const buttonProps = { ...rest } as unknown as ButtonOptions;

      return (
        <Button
          {...buttonProps}
          aria-disabled={isDisabled || rest['aria-disabled']}
          as={buttonTag}
          className={className}
          data-link-as-button={asButton || displayAsButton}
          displayAsButton={displayAsButton}
          href={currentHrefToValue}
          // @ts-ignore FIXME: handle correct type change for button
          onClick={handleOnClick}
          size={buttonSize}
          to={currentHrefToValue}
          type={type as ButtonTypes}
          variant={variant as Component.Type['Button']}
        >
          {linkLabel}
        </Button>
      );
    }

    if (isInternalLink && !noPrefix && !newTab && !excludeFromGatsby) {
      return (
        <StyledGatsbyLink
          {...rest}
          aria-disabled={isDisabled || rest['aria-disabled']}
          className={className}
          data-link-as-button={!asButton && displayAsButton ? true : undefined}
          data-link-as-link={!asButton && !displayAsButton ? true : undefined}
          displayBlock={displayBlock}
          hasArrow={hasArrow}
          iconColor={iconColor}
          iconHoverColor={iconHoverColor}
          isInverse={isInverse}
          onClick={handleOnClick}
          rel={relProps}
          target={targetProps}
          to={redirect || hrefTo || ''}
          underline={underline}
          variant={variant as Component.Type['Link']}
        >
          {linkLabel}
        </StyledGatsbyLink>
      );
    }

    return (
      <StyledLink
        {...rest}
        ref={forwardedRef}
        aria-disabled={isDisabled || rest['aria-disabled']}
        className={className}
        data-link-as-button={!asButton && displayAsButton ? true : undefined}
        data-link-as-link={!asButton && !displayAsButton ? true : undefined}
        displayBlock={displayBlock}
        hasArrow={hasArrow}
        href={currentHrefToValue}
        isInverse={isInverse}
        onClick={handleOnClick}
        rel={relProps}
        target={targetProps}
        underline={underline}
        variant={variant}
      >
        {linkLabel}
        {hasArrow && !asButton && !displayAsButton && (
          <span
            css={{
              label: 'hasArrow',
              position: 'relative',
              fontSize: '0.75em',
              marginLeft: '0.5em',
              display: 'inline-block',
              transition: 'transform 0.15s ease-out',
              color: 'inherit',
            }}
            /* eslint-disable-next-line react/jsx-sort-props */
            className="hasArrow"
          >
            <Icon color={iconColor} hoverColor={iconHoverColor} type="ArrowRight" />
          </span>
        )}
      </StyledLink>
    );
  },
);

// manually adding display name or it goes missing on build
LinkComponent.displayName = 'Link';
const Link = decorate<LinkProps>(LinkComponent);

export { Link };
export default Link;
